import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { resetRedirectUrl } from './auth-redirect';

export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export function onAuthChange(cb: (user: User | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    cb(session?.user ?? null);
  });
  return () => data.subscription.unsubscribe();
}

/**
 * Email a password-reset link.
 *
 * The link returns to the address the request came from (`origin`), which must
 * be in Supabase's redirect allowlist — see MULTI_FIRM_PLAN.md, Phase 21.
 *
 * Supabase does not reveal whether the email belongs to an account, and
 * neither should the caller's UI: this resolves the same way either way. It
 * throws only for a genuine failure, such as the per-address rate limit.
 */
export async function requestPasswordReset(email: string, origin: string = location.origin): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: resetRedirectUrl(origin),
  });
  if (error) throw error;
}

/** Set a new password for the signed-in user — here, the recovery session. */
export async function updatePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

/**
 * Spend a one-time email link (token_hash from the branded templates) and
 * start the session it grants. Called only when the person presses Continue —
 * see confirmCopy() in auth-redirect.ts for why never on page load.
 */
export async function verifyEmailLink(
  tokenHash: string, purpose: 'recovery' | 'invite',
): Promise<{ userId: string; email: string | null }> {
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: purpose });
  if (error) throw error;
  if (!data.user) throw new Error('That link could not be used.');
  return { userId: data.user.id, email: data.user.email ?? null };
}
